import { useEffect, useState } from 'react';
import {
  Card,
  Text,
  Badge,
  EmptyState,
  Spinner,
  Banner,
  Box,
  Layout,
  BlockStack,
  InlineStack,
  Button,
  Icon,
  IndexTable,
  Link,
  Collapsible,
  InlineGrid,
} from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import katanaApi, { KatanaApiError } from '@/services/katana';
import type { PurchaseOrder, Supplier } from '@/types/katana';

interface SupplierGroup {
  supplierId: number;
  supplierName: string;
  orders: PurchaseOrder[];
  totalValue: number;
  orderCount: number;
}

export default function OutsourcedPOList() {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [supplierGroups, setSupplierGroups] = useState<SupplierGroup[]>([]);
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading suppliers first to ensure proper mapping...');
      
      // Load suppliers first to ensure they're available for order mapping
      const suppliersData = await katanaApi.getSuppliers();
      console.log(`✅ Fetched ${suppliersData.length} suppliers for outsourced orders`);
      
      // Create supplier mapping
      const supplierMap = suppliersData.reduce((acc, supplier: Supplier) => {
        acc[supplier.id] = supplier.name;
        return acc;
      }, {} as Record<number, string>);
      
      console.log('📋 Supplier mapping created:', Object.keys(supplierMap).length, 'suppliers');
      
      // Small delay to prevent API overload
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Then load outsourced purchase orders
      console.log('🔄 Loading outsourced purchase orders...');
      const purchaseOrdersData = await katanaApi.getPurchaseOrders(
        { 
          status: ['NOT_RECEIVED'],
          entity_type: ['outsourced'] // Only outsourced purchase orders
        },
        { field: 'order_created_date', direction: 'desc' },
        1,
        100 // Load more records for comprehensive view
      );
      
      console.log(`✅ Fetched ${purchaseOrdersData.data.length} outsourced purchase orders`);
      setPurchaseOrders(purchaseOrdersData.data);
      
      // Group orders by supplier with validated supplier mapping
      const groups = groupOrdersBySupplier(purchaseOrdersData.data, supplierMap);
      console.log(`📊 Created ${groups.length} supplier groups for outsourced orders`);
      setSupplierGroups(groups);
      
      // Initially expand all suppliers for better UX
      setExpandedSuppliers(new Set(groups.map(group => group.supplierId)));
    } catch (err) {
      if (err instanceof KatanaApiError) {
        if (err.code === 'NOT_CONFIGURED') {
          setError('Katana API not configured. Please configure your Katana API credentials in the Settings page.');
        } else if (err.code === 'NETWORK_ERROR') {
          setError('Unable to connect to Katana API. Please check your API credentials and server URL in Settings.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to load outsourced purchase orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const groupOrdersBySupplier = (orders: PurchaseOrder[], supplierMap: Record<number, string>): SupplierGroup[] => {
    console.log('🔍 Grouping outsourced orders by supplier:', {
      ordersCount: orders.length,
      supplierMapSize: Object.keys(supplierMap).length,
      sampleOrder: orders[0] ? { id: orders[0].id, supplier_id: orders[0].supplier_id } : null
    });
    
    const groups = orders.reduce((acc, order) => {
      const supplierId = order.supplier_id;
      
      // Validate supplier ID exists
      if (!supplierId) {
        console.warn('⚠️ Outsourced order missing supplier_id:', order.order_no);
        return acc;
      }
      
      const supplierName = supplierMap[supplierId];
      
      // Warn if supplier name not found in mapping
      if (!supplierName) {
        console.warn('⚠️ Outsourced supplier not found in mapping:', {
          supplierId,
          orderNo: order.order_no,
          availableSuppliers: Object.keys(supplierMap).slice(0, 5)
        });
      }
      
      const finalSupplierName = supplierName || 'Unknown Supplier';
      
      if (!acc[supplierId]) {
        acc[supplierId] = {
          supplierId,
          supplierName: finalSupplierName,
          orders: [],
          totalValue: 0,
          orderCount: 0,
        };
      }
      
      acc[supplierId].orders.push(order);
      acc[supplierId].totalValue += order.total;
      acc[supplierId].orderCount += 1;
      
      return acc;
    }, {} as Record<number, SupplierGroup>);

    const groupArray = Object.values(groups).sort((a, b) => b.totalValue - a.totalValue);
    console.log('📊 Outsourced supplier grouping completed:', {
      groupCount: groupArray.length,
      totalOrders: groupArray.reduce((sum, group) => sum + group.orderCount, 0),
      unknownSuppliers: groupArray.filter(group => group.supplierName === 'Unknown Supplier').length
    });
    
    // Convert to array and sort by total value (highest first)
    return groupArray;
  };

  const toggleSupplierExpansion = (supplierId: number) => {
    const newExpanded = new Set(expandedSuppliers);
    if (newExpanded.has(supplierId)) {
      newExpanded.delete(supplierId);
    } else {
      newExpanded.add(supplierId);
    }
    setExpandedSuppliers(newExpanded);
  };

  const expandAllSuppliers = () => {
    setExpandedSuppliers(new Set(supplierGroups.map(group => group.supplierId)));
  };

  const collapseAllSuppliers = () => {
    setExpandedSuppliers(new Set());
  };

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getTotalMetrics = () => {
    const totalOrders = purchaseOrders.length;
    const totalValue = purchaseOrders.reduce((sum, order) => sum + order.total, 0);
    const uniqueSuppliers = new Set(purchaseOrders.map(order => order.supplier_id)).size;
    
    return {
      totalOrders,
      totalValue,
      uniqueSuppliers,
    };
  };

  if (loading) {
    return (
      <>
        {/* Page header card for development mode */}
        <Card>
          <Box padding="400">
            <BlockStack gap="200">
              <Text as="h1" variant="headingLg">Outsourced Purchase Orders</Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Manufacturing orders sent to external suppliers
              </Text>
            </BlockStack>
          </Box>
        </Card>
        
        <Layout>
          <Layout.Section>
            <Card>
              <Box padding="800">
                <BlockStack gap="400" align="center">
                  <Spinner size="large" />
                  <Text as="p" variant="bodyMd">
                    Loading outsourced purchase orders...
                  </Text>
                </BlockStack>
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      </>
    );
  }

  if (error) {
    return (
      <>
        {/* Page header card for development mode */}
        <Card>
          <Box padding="400">
            <BlockStack gap="200">
              <Text as="h1" variant="headingLg">Outsourced Purchase Orders</Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Manufacturing orders sent to external suppliers
              </Text>
            </BlockStack>
          </Box>
        </Card>
        
        <Layout>
          <Layout.Section>
            <Banner tone="critical" title="Unable to load outsourced purchase orders">
              <p>{error}</p>
              <p>
                Please check your Katana API configuration in{' '}
                <Link url="/settings">Settings</Link>.
              </p>
            </Banner>
          </Layout.Section>
        </Layout>
      </>
    );
  }

  if (purchaseOrders.length === 0) {
    return (
      <EmptyState
        heading="No pending outsourced purchase orders"
        image="https://cdn.shopify.com/s/files/1/0262/4074/files/emptystate-files.png"
      >
        <p>There are currently no outsourced purchase orders that need to be received.</p>
      </EmptyState>
    );
  }

  return (
    <BlockStack gap="400">
      {/* Summary Metrics */}
      <Card>
        <Box padding="400">
          <InlineGrid columns={{ xs: '1fr', sm: '1fr 1fr 1fr' }} gap="400">
            <BlockStack gap="100" align="center">
              <Text as="p" variant="bodyMd" tone="subdued">Total Orders</Text>
              <Text as="p" variant="headingLg">{getTotalMetrics().totalOrders}</Text>
            </BlockStack>
            <BlockStack gap="100" align="center">
              <Text as="p" variant="bodyMd" tone="subdued">Total Value</Text>
              <Text as="p" variant="headingLg">{formatCurrency(getTotalMetrics().totalValue)}</Text>
            </BlockStack>
            <BlockStack gap="100" align="center">
              <Text as="p" variant="bodyMd" tone="subdued">Suppliers</Text>
              <Text as="p" variant="headingLg">{getTotalMetrics().uniqueSuppliers}</Text>
            </BlockStack>
          </InlineGrid>
        </Box>
      </Card>

      {/* Orders by Supplier */}
      <Card>
        <Box padding="400">
          <BlockStack gap="200">
            <InlineGrid columns={{xs: "1fr", sm: "1fr auto"}} gap="400" alignItems="center">
              <div>
                <Text as="h2" variant="headingMd">Outsourced Orders by Supplier</Text>
                <Text as="p" tone="subdued">
                  {getTotalMetrics().totalOrders} not received outsourced manufacturing orders from {getTotalMetrics().uniqueSuppliers} suppliers
                </Text>
              </div>
              <InlineStack align="end" gap="200">
                <Button onClick={expandAllSuppliers} size="slim">Expand All</Button>
                <Button onClick={collapseAllSuppliers} size="slim">Collapse All</Button>
              </InlineStack>
            </InlineGrid>
          </BlockStack>
        </Box>

        <div>
          {supplierGroups.map(({ supplierId, supplierName, orders, totalValue, orderCount }) => {
            const isExpanded = expandedSuppliers.has(supplierId);
            const resourceName = {
              singular: 'order',
              plural: 'orders',
            };

            return (
              <div key={supplierId}>
                <div
                  onClick={() => toggleSupplierExpansion(supplierId)}
                  role="button"
                  aria-expanded={isExpanded}
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleSupplierExpansion(supplierId)}
                  style={{
                    padding: 'var(--p-space-400)',
                    borderBlockStart: '1px solid var(--p-color-border)',
                    cursor: 'pointer',
                  }}
                >
                  <InlineGrid columns="auto 1fr auto" gap="400" alignItems="center">
                    <Icon source={isExpanded ? ChevronUpIcon : ChevronDownIcon} />
                    <Text as="p" variant="bodyLg" fontWeight="semibold">{supplierName}</Text>
                    <InlineStack gap="400" align="end">
                      <Badge tone="info">{`${orderCount} ${orderCount > 1 ? 'orders' : 'order'}`}</Badge>
                      <Badge tone="success">{formatCurrency(totalValue)}</Badge>
                    </InlineStack>
                  </InlineGrid>
                </div>

                <Collapsible open={isExpanded} id={`supplier-collapsible-${supplierId}`}>
                  <IndexTable
                    resourceName={resourceName}
                    itemCount={orders.length}
                    headings={[
                      { title: 'Order #' },
                      { title: 'Status' },
                      { title: 'Total', alignment: 'end' },
                      { title: 'Expected Arrival', alignment: 'end' },
                      { title: 'Order Date', alignment: 'end' },
                    ]}
                    selectable={false}
                  >
                    {orders.map((order, index) => (
                      <IndexTable.Row
                        id={String(order.id)}
                        key={order.id}
                        position={index}
                      >
                        <IndexTable.Cell>
                          <Link
                            url={`https://factory.katanamrp.com/purchases-outsource/${order.id}`}
                            target="_blank"
                            removeUnderline
                          >
                            {order.order_no}
                          </Link>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Badge tone={order.status === 'NOT_RECEIVED' ? 'attention' : 'success'}>
                            {order.status.replace(/_/g, ' ')}
                          </Badge>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" alignment="end" numeric>
                            {formatCurrency(order.total, order.currency)}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" alignment="end">
                            {order.expected_arrival_date
                              ? new Date(order.expected_arrival_date).toLocaleDateString()
                              : 'N/A'}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Text as="span" alignment="end">
                            {new Date(order.order_created_date).toLocaleDateString()}
                          </Text>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    ))}
                  </IndexTable>
                  <div style={{
                    borderBlockStart: '1px solid var(--p-color-border)',
                    borderBlockEnd: '1px solid var(--p-color-border)',
                    padding: 'var(--p-space-400)',
                    backgroundColor: 'var(--p-color-bg-surface-secondary)'
                  }}>
                    <InlineStack align="end">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {orders.length} orders • Total: {formatCurrency(totalValue)}
                      </Text>
                    </InlineStack>
                  </div>
                </Collapsible>
              </div>
            );
          })}
        </div>
      </Card>
    </BlockStack>
  );
}
